<template>
    <div class="wa-ui-page" :class="{ 'wa-ui-page--mobile': mobileMode }">

        <!-- DESKTOP MODE: yan yana 3 panel -->
        <template v-if="!mobileMode">
            <WAChannelSwitcher
                v-if="showChannelSwitcher"
                :channels="visibleChannels"
                :selected-channel-id="selectedChannelId"
                :total-unread="totalUnreadCount"
                @change="onChannelChange"
                @open-settings="openChannelSettings"
            />
            <WAContactList
                ref="contactList"
                :selected-contact="selectedContact"
                :selected-channel="selectedChannel"
                :hide-inbox="qp.hideInbox"
                @contact-selected="onContactSelected"
            />
            <WAChatArea
                v-if="selectedContact && selectedContact.lastConversation"
                :key="selectedContact._id"
                :contact="selectedContact"
                :channel="getContactChannel(selectedContact)"
            />
            <WAEmptyState v-else />
        </template>

        <!-- MOBILE MODE: tek panel, animasyonlu geçiş -->
        <template v-else>
            <transition :name="slideDirection">
                <!-- Liste ekranı -->
                <div v-if="mobileView === 'list'" key="list" class="wa-mobile-screen">
                    <!-- Üstte kanal sekmeleri (hideLines değilse ve birden fazla kanal varsa) -->
                    <div class="wa-mobile-channel-tabs" v-if="showChannelSwitcher">
                        <button
                            v-if="!qp.forcedChannelId"
                            class="wa-mobile-channel-tab"
                            :class="{ 'wa-mobile-channel-tab--active': selectedChannelId === null }"
                            @click="onChannelChange(null)"
                        >Tümü</button>
                        <button
                            v-for="ch in visibleChannels"
                            :key="ch._id"
                            class="wa-mobile-channel-tab"
                            :class="{ 'wa-mobile-channel-tab--active': selectedChannelId === ch._id }"
                            @click="onChannelChange(ch._id)"
                        >{{ ch.name || getChannelShortLabel(ch.type) }}</button>
                    </div>
                    <WAContactList
                        ref="contactList"
                        :selected-contact="selectedContact"
                        :selected-channel="selectedChannel"
                        :mobile-mode="true"
                        :hide-inbox="qp.hideInbox"
                        @contact-selected="onMobileContactSelected"
                    />
                </div>

                <!-- Sohbet ekranı -->
                <div v-else-if="mobileView === 'chat'" key="chat" class="wa-mobile-screen">
                    <WAChatArea
                        v-if="selectedContact && selectedContact.lastConversation"
                        :key="selectedContact._id"
                        :contact="selectedContact"
                        :channel="getContactChannel(selectedContact)"
                        :mobile-mode="true"
                        @back="goBackToList"
                    />
                </div>
            </transition>
        </template>
    </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex";
import WAChannelSwitcher from "./components/WAChannelSwitcher.vue";
import WAContactList from "./components/WAContactList.vue";
import WAChatArea from "./components/WAChatArea.vue";
import WAEmptyState from "./components/WAEmptyState.vue";

const WA_FULLSCREEN_CLASS = "wa-fullscreen-mode";
const ESC_DOUBLE_PRESS_DELAY = 400;

export default {
    name: "WhatsappUIPage",
    components: {
        WAChannelSwitcher,
        WAContactList,
        WAChatArea,
        WAEmptyState,
    },
    props: {
        mobileMode: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        selectedChannelId: null,
        mobileView: "list",        // 'list' | 'chat'
        slideDirection: "slide-left", // transition name
        escPressCount: 0,
        escTimerId: null,
    }),
    computed: {
        ...mapState({
            contacts: (state) => state.contact.contacts,
            selectedContact: (state) => state.contact.selectedContact,
        }),
        ...mapGetters({
            whatsappChannels: "channel/getVisibleWhatsappChannels",
        }),
        // URL query param'larını parse et (hi/hideInbox, hl/hideLines, c=channelId)
        qp() {
            const q = this.$route && this.$route.query || {};
            return {
                hideInbox: "hi" in q || "hideInbox" in q,
                hideLines: "hl" in q || "hideLines" in q,
                forcedChannelId: q.c || null,
            };
        },
        // hideLines veya forcedChannelId varsa kanal seçiciyi gösterme
        showChannelSwitcher() {
            if (this.qp.hideLines || this.qp.forcedChannelId) return false;
            return this.whatsappChannels.length > 1;
        },
        // Görünür kanal listesi: forcedChannelId varsa sadece o kanal
        visibleChannels() {
            if (this.qp.forcedChannelId) {
                return this.whatsappChannels.filter((c) => c._id === this.qp.forcedChannelId);
            }
            return this.whatsappChannels;
        },
        selectedChannel() {
            if (!this.selectedChannelId) return null;
            return this.whatsappChannels.find((c) => c._id === this.selectedChannelId) || null;
        },
        totalUnreadCount() {
            return this.contacts.filter((c) => c.unread).length;
        },
    },
    mounted() {
        this.$store.dispatch("app/setWhatsappUIMode", true);
        this.enableFullscreen();
        // c=channelId varsa başlangıçta o kanal seçili gelsin
        if (this.qp.forcedChannelId) {
            this.selectedChannelId = this.qp.forcedChannelId;
        }
        this.loadWhatsappContacts();
        if (!this.mobileMode) {
            document.addEventListener("keydown", this.onKeyDown);
        }
    },
    beforeDestroy() {
        this.$store.dispatch("app/setWhatsappUIMode", false);
        this.disableFullscreen();
        document.removeEventListener("keydown", this.onKeyDown);
        if (this.escTimerId) clearTimeout(this.escTimerId);
    },
    methods: {
        ...mapActions({
            getFilteredContacts: "contact/getFilteredContacts",
        }),
        enableFullscreen() {
            const el = document.getElementById("contents");
            if (el) el.classList.add(WA_FULLSCREEN_CLASS);
            document.body.classList.add("supsis-wa-ui-active");
        },
        disableFullscreen() {
            const el = document.getElementById("contents");
            if (el) el.classList.remove(WA_FULLSCREEN_CLASS);
            document.body.classList.remove("supsis-wa-ui-active");
        },
        onKeyDown(e) {
            if (e.key !== "Escape") return;
            this.escPressCount++;
            if (this.escTimerId) clearTimeout(this.escTimerId);
            if (this.escPressCount >= 2) {
                this.escPressCount = 0;
                this.toggleFullscreen();
                return;
            }
            this.escTimerId = setTimeout(() => {
                this.escPressCount = 0;
            }, ESC_DOUBLE_PRESS_DELAY);
        },
        toggleFullscreen() {
            const el = document.getElementById("contents");
            if (el) el.classList.toggle(WA_FULLSCREEN_CLASS);
        },
        async loadWhatsappContacts() {
            const filters = [{ field: "platform", operator: "equals", value: ["whatsapp"] }];
            if (this.selectedChannelId) {
                filters.push({ field: "channel", operator: "equals", value: [this.selectedChannelId] });
            }
            await this.getFilteredContacts({
                source: "WhatsappUI.loadWhatsappContacts",
                filters,
                limit: 30,
                state: "all",
            });
        },
        onChannelChange(channelId) {
            this.selectedChannelId = channelId;
            this.$nextTick(() => {
                if (this.$refs.contactList) {
                    this.$refs.contactList.loadContacts(true);
                }
            });
        },
        onContactSelected() {
            // desktop: WAContactList already called setSelectedContact
        },
        onMobileContactSelected() {
            // mobile: liste → sohbet animasyonu
            this.slideDirection = "slide-left";
            this.$nextTick(() => {
                this.mobileView = "chat";
            });
        },
        goBackToList() {
            this.slideDirection = "slide-right";
            this.$nextTick(() => {
                this.mobileView = "list";
            });
        },
        getContactChannel(contact) {
            if (!contact) return null;
            const channelId = contact.channel || this.$_.get(contact, "lastConversation.channel");
            if (!channelId) return this.selectedChannel;
            return this.whatsappChannels.find((c) => c._id === channelId) || this.selectedChannel;
        },
        getChannelShortLabel(type) {
            const labels = {
                "whatsapp-business-api": "WABA",
                "whatsapp-business-api-supsis": "WABA",
                "whatsapp-unipile": "Unipile",
                "whatsapp-v2": "QR",
                "whatsapp-qr": "QR",
            };
            return labels[type] || "WA";
        },
        openChannelSettings() {
            this.$router.push({ path: "/settings/integrations" });
        },
    },
};
</script>

<!-- Unscoped: #contents.wa-fullscreen-mode altındaki Header ve Sidebar'ı gizler -->
<!-- body.supsis-wa-ui-active: resize'dan bağımsız kalıcı gizleme -->
<style lang="scss">
#contents.wa-fullscreen-mode {
    height: 100vh !important;

    #header {
        display: none !important;
    }

    #sidebar {
        display: none !important;
    }

    .main-content {
        height: 100vh !important;
        padding-top: 0 !important;
        margin-top: 0 !important;
    }

    .content-view,
    .content-view.menu-active {
        margin-left: 0 !important;
        padding-left: 0 !important;
        width: 100% !important;
        border-top-left-radius: 0 !important;
        border-top: none !important;
        border-left: none !important;
    }

    .content-view-child {
        height: 100% !important;
    }
}

body.supsis-wa-ui-active {
    #header {
        display: none !important;
    }

    #sidebar {
        display: none !important;
    }

    .content-view,
    .content-view.menu-active {
        margin-left: 0 !important;
        padding-left: 0 !important;
        width: 100% !important;
        border-top-left-radius: 0 !important;
        border-top: none !important;
        border-left: none !important;
    }
}
</style>

<style lang="scss" scoped>
.wa-ui-page {
    display: flex;
    height: 100vh;
    width: 100%;
    overflow: hidden;
    background: #f0f2f5;
    position: relative;

    // ---- Mobile mode ----
    &--mobile {
        display: block;
        position: relative;
        overflow: hidden;
    }
}

// Mobile ekranları (her biri tam boyut, transition ile geçiş)
.wa-mobile-screen {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background: #fff;
    will-change: transform;
}

// Kanal sekmeleri (mobile üst tab bar)
.wa-mobile-channel-tabs {
    display: flex;
    overflow-x: auto;
    background: #202c33;
    padding: 0 8px;
    flex-shrink: 0;
    scrollbar-width: none;

    &::-webkit-scrollbar { display: none; }
}

.wa-mobile-channel-tab {
    flex-shrink: 0;
    padding: 10px 14px;
    background: none;
    border: none;
    color: rgba(255,255,255,0.6);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    outline: none;
    white-space: nowrap;
    transition: color 0.15s, border-color 0.15s;

    &--active {
        color: #25D366;
        border-bottom-color: #25D366;
    }

    &:hover:not(&--active) {
        color: rgba(255,255,255,0.85);
    }
}

// ---- Slide LEFT: liste → sohbet ----
.slide-left-enter-active,
.slide-left-leave-active {
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-left-enter {
    transform: translateX(100%);
}

.slide-left-leave-to {
    transform: translateX(-100%);
}

// ---- Slide RIGHT: sohbet → liste ----
.slide-right-enter-active,
.slide-right-leave-active {
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-right-enter {
    transform: translateX(-100%);
}

.slide-right-leave-to {
    transform: translateX(100%);
}
</style>
